import { clsx } from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Inbox, RefreshCw, TriangleAlert } from "lucide-react";

/* ---------------------------------------------------------------------------
 * Brand
 * ------------------------------------------------------------------------- */

export function Bolt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z" />
    </svg>
  );
}

export function Logo({ wordmark = true }: { wordmark?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2.5 select-none">
      <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-volt-400 text-ink-950 shadow-volt">
        <Bolt className="h-[18px] w-[18px]" />
      </span>
      {wordmark && (
        <span className="font-display text-[17px] font-bold tracking-tight text-mist-100">
          petir<span className="text-volt-400">.</span>
        </span>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * Button
 * ------------------------------------------------------------------------- */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "outline" | "ghost";
  size?: "md" | "sm";
};

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={clsx(
        "btn",
        variant === "primary" && "btn-primary",
        variant === "outline" && "btn-outline",
        variant === "ghost" && "btn-ghost",
        size === "sm" && "btn-sm",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------------------
 * Feedback primitives
 * ------------------------------------------------------------------------- */

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={clsx(
        "inline-block h-4 w-4 animate-spin rounded-full border-2 border-mist-600/30 border-t-volt-400",
        className,
      )}
    />
  );
}

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-mist-400">
      <Spinner className="h-6 w-6" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div className="card flex flex-col items-center justify-center gap-4 px-6 py-14 text-center animate-rise">
      <span className="grid h-12 w-12 place-items-center rounded-xl bg-red-400/10 text-red-400">
        <TriangleAlert className="h-6 w-6" />
      </span>
      <div className="space-y-1">
        <p className="font-medium text-mist-100">Something went wrong</p>
        <p className="text-sm text-mist-400">{message || "Could not reach the Petir API."}</p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5" /> Try again
        </Button>
      )}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="card flex flex-col items-center justify-center gap-3 px-6 py-14 text-center animate-rise">
      <span className="grid h-12 w-12 place-items-center rounded-xl bg-ink-800 text-mist-400">
        {icon ?? <Inbox className="h-6 w-6" />}
      </span>
      <div className="space-y-1">
        <p className="font-medium text-mist-100">{title}</p>
        {description && <p className="max-w-sm text-sm text-mist-400">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Status & severity
 * ------------------------------------------------------------------------- */

const STATUS_STYLES: Record<string, { dot: string; text: string; chip: string }> = {
  running: { dot: "bg-sky-400", text: "text-sky-300", chip: "border-sky-400/20 bg-sky-400/10" },
  completed: { dot: "bg-emerald-400", text: "text-emerald-300", chip: "border-emerald-400/20 bg-emerald-400/10" },
  failed: { dot: "bg-red-400", text: "text-red-300", chip: "border-red-400/20 bg-red-400/10" },
  queued: { dot: "bg-mist-500", text: "text-mist-300", chip: "border-mist-500/20 bg-mist-500/10" },
};

export function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.queued;
  return (
    <span className={clsx("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium", s.chip, s.text)}>
      <span className={clsx("h-1.5 w-1.5 rounded-full", s.dot, status === "running" && "animate-pulse")} />
      <span className="capitalize">{status}</span>
    </span>
  );
}

export const SEVERITY_META: Record<string, { dot: string; text: string; chip: string }> = {
  critical: { dot: "bg-red-400", text: "text-red-300", chip: "border-red-400/25 bg-red-400/10" },
  high: { dot: "bg-orange-400", text: "text-orange-300", chip: "border-orange-400/25 bg-orange-400/10" },
  medium: { dot: "bg-amber-300", text: "text-amber-200", chip: "border-amber-300/25 bg-amber-300/10" },
  low: { dot: "bg-sky-400", text: "text-sky-300", chip: "border-sky-400/25 bg-sky-400/10" },
  info: { dot: "bg-mist-400", text: "text-mist-300", chip: "border-mist-400/25 bg-mist-400/10" },
};

export function SeverityBadge({ severity }: { severity: string }) {
  const key = (severity || "info").toLowerCase();
  const s = SEVERITY_META[key] ?? SEVERITY_META.info;
  return (
    <span className={clsx("inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium", s.chip, s.text)}>
      <span className={clsx("h-1.5 w-1.5 rounded-full", s.dot)} />
      <span className="capitalize">{severity}</span>
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * Layout helpers
 * ------------------------------------------------------------------------- */

export function PageHeader({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-[22px] font-semibold leading-tight tracking-tight text-mist-100">
          {title}
        </h1>
        {description && <p className="mt-1 text-sm text-mist-400">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  accent = "volt",
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  icon?: ReactNode;
  accent?: "volt" | "red" | "sky" | "emerald" | "orange";
}) {
  const accents: Record<string, string> = {
    volt: "text-volt-400 bg-volt-400/10",
    red: "text-red-400 bg-red-400/10",
    sky: "text-sky-400 bg-sky-400/10",
    emerald: "text-emerald-400 bg-emerald-400/10",
    orange: "text-orange-400 bg-orange-400/10",
  };
  return (
    <div className="card group p-5 transition-colors hover:border-line-strong">
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-medium text-mist-400">{label}</p>
        {icon && (
          <span className={clsx("grid h-8 w-8 place-items-center rounded-lg", accents[accent])}>
            {icon}
          </span>
        )}
      </div>
      <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-mist-100">{value}</p>
      {hint && <p className="mt-1 text-xs text-mist-500">{hint}</p>}
    </div>
  );
}
